import { useMemo } from 'react';
import { Paper, Box } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import {
  formatRupeesIndianCompact,
  formatIndianAuto,
  premiumTooltip,
  standardGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';
import { getCurrency as currencySelector } from 'utils/redux/reducer/authentication-slice';

const norm = s => String(s || '').toLowerCase();
const toNum = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const sumArr = arr => (arr || []).reduce((s, v) => s + toNum(v), 0);

const ASSIGNED_GRAY = '#78909C';
const CONFIRMED_GREEN = '#66BB6A';
const TENTATIVE_AMBER = '#FFC107';
const ACTUALIZED_PURPLE = '#9575CD';

function findDsIdx(datasets, needle) {
  return datasets.findIndex(d => norm(String(d.label)).includes(needle));
}

function extractSourceData(chartData) {
  const labels = chartData?.labels || [];
  const ds = chartData?.datasets || [];

  const iAssign = findDsIdx(ds, 'assign');
  const iConf = findDsIdx(ds, 'confirm');
  const iAct = findDsIdx(ds, 'actual');
  const iTent = findDsIdx(ds, 'tent');

  const assigned = labels.map((_, i) => iAssign >= 0 ? toNum(ds[iAssign].data?.[i]) : 0);
  const confirmed = labels.map((_, i) => iConf >= 0 ? toNum(ds[iConf].data?.[i]) : 0);
  const actualized = labels.map((_, i) => iAct >= 0 ? toNum(ds[iAct].data?.[i]) : 0);
  const tentative = labels.map((_, i) => iTent >= 0 ? toNum(ds[iTent].data?.[i]) : 0);

  return { labels: labels.map(String), assigned, confirmed, tentative, actualized };
}

function tooltipFormatter(params) {
  if (!Array.isArray(params) || !params.length) return '';
  const title = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${params[0].axisValueLabel}</div>`;

  const row = (label, value, color) => {
    const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>`;
    return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${dot}<span style="flex:1">${label}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(value)}</span></div>`;
  };

  let assignedVal = 0, confVal = 0, tentVal = 0, actVal = 0;
  params.forEach(p => {
    const L = norm(p.seriesName);
    if (L.includes('assign')) assignedVal = toNum(p.value);
    else if (L.includes('actual')) actVal = toNum(p.value);
    else if (L.includes('confirm')) confVal = toNum(p.value);
    else if (L.includes('tent')) tentVal = toNum(p.value);
  });

  const pipeline = confVal + tentVal;

  let html = title;
  html += row('Assigned', assignedVal, ASSIGNED_GRAY);
  html += `<div style="border-top:1px solid rgba(148,163,184,0.15);margin:4px 0"></div>`;
  html += row('Confirmed', confVal, CONFIRMED_GREEN);
  html += row('Tentative', tentVal, TENTATIVE_AMBER);
  if (pipeline > 0 && assignedVal > 0) {
    const pipelinePct = Math.round((pipeline / assignedVal) * 1000) / 10;
    html += `<div style="color:#94a3b8;font-size:11px;margin:2px 0 2px 14px">Pipeline: ${formatRupeesIndianCompact(pipeline)} (${pipelinePct}%)</div>`;
  }
  html += `<div style="border-top:1px solid rgba(148,163,184,0.15);margin:4px 0"></div>`;
  html += row('Actualized', actVal, ACTUALIZED_PURPLE);
  if (actVal > 0 && assignedVal > 0) {
    const actPct = Math.round((actVal / assignedVal) * 1000) / 10;
    html += `<div style="color:#94a3b8;font-size:11px;margin:2px 0 0 14px">Achievement: ${actPct}%</div>`;
  }
  return html;
}

function buildOption(data) {
  const { labels, assigned, confirmed, tentative, actualized } = data;
  if (!labels.length) return {};

  return {
    ...defaultAnimation,
    grid: standardGrid,
    tooltip: {
      ...premiumTooltip,
      formatter: tooltipFormatter,
    },
    legend: { show: false },
    xAxis: {
      ...indianCategoryAxis,
      data: labels,
      axisLabel: {
        ...indianCategoryAxis.axisLabel,
        rotate: labels.length > 6 ? 30 : 0,
      },
    },
    yAxis: indianValueAxis,
    series: [
      {
        name: 'Assigned',
        type: 'bar',
        data: assigned,
        barWidth: '16%',
        itemStyle: { color: ASSIGNED_GRAY, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Confirmed',
        type: 'bar',
        data: confirmed,
        barWidth: '16%',
        itemStyle: { color: CONFIRMED_GREEN, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Actualized',
        type: 'bar',
        data: actualized,
        barWidth: '16%',
        itemStyle: { color: ACTUALIZED_PURPLE, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Tentative',
        type: 'bar',
        data: tentative,
        barWidth: '16%',
        itemStyle: { color: TENTATIVE_AMBER, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
    ],
  };
}

const SourceOverviewChart = ({
  data,
  handleExport,
  showButton = true,
}) => {
  const currencyContext = useSelector(currencySelector);
  const currencyKey = `${currencyContext?.currencySymbol || ''}|${currencyContext?.currency || ''}|${currencyContext?.currencyLocale || ''}`;
  const extracted = useMemo(() => extractSourceData(data), [data]);
  const option = useMemo(() => buildOption(extracted), [extracted]);

  const displayName = data?.name || 'Source Performance';
  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Assigned vs confirmed, tentative & actualized revenue by source';

  const totalAssigned = sumArr(extracted.assigned);
  const totalConfirmed = sumArr(extracted.confirmed);
  const totalTentative = sumArr(extracted.tentative);
  const totalPipeline = totalConfirmed + totalTentative;
  const totalActualized = sumArr(extracted.actualized);
  const pipelinePct = totalAssigned > 0 ? Math.round((totalPipeline / totalAssigned) * 1000) / 10 : 0;
  const actPct = totalAssigned > 0 ? Math.round((totalActualized / totalAssigned) * 1000) / 10 : 0;

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        items={apiKpis || [
          { label: 'Total Assigned', value: formatRupeesIndianCompact(totalAssigned) },
          { label: 'Pipeline', value: `${formatRupeesIndianCompact(totalPipeline)} (${pipelinePct}%)` },
          { label: 'Actualized', value: `${formatRupeesIndianCompact(totalActualized)} (${actPct}%)` },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={reportChartCanvasSx}>
        <ReactECharts key={currencyKey} option={option} style={{ width: '100%', height: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default SourceOverviewChart;
